import React from "react";
import { Row, Col } from "reactstrap";
import { AlertColor } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import PublicIcon from "@mui/icons-material/Public";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowsRotate } from "@fortawesome/free-solid-svg-icons";
import PrimaryButton from "../../../../../../../toolkit/primaryButton";
import { useLoading } from "../../../../../../../hooks/useLoading";
import { publishCharityCustomization } from "../../../../../../../services/publishCharityCustomization";
import IGlobalConfig from "../../../../../../../types/IGlobalConfig";
import s from "../styles.scss";

interface SiteActionButtonsProps {
  charityId?: string;
  config?: IGlobalConfig;
  serviceAccessToken?: string;
  whitelabelUrl?: string | null;
  onPublishSuccess?: () => void;
  showSnackbar?: (message: string, severity?: AlertColor) => void;
  publishDisabled?: boolean;
}

const normalizeSiteUrl = (url?: string | null): string | null => {
  if (!url || typeof url !== "string") {
    return null;
  }
  const trimmed = url.trim();
  if (!trimmed) {
    return null;
  }
  return trimmed.replace(/\/+$/, "");
};

const SiteActionButtons: React.FC<SiteActionButtonsProps> = ({
  charityId,
  config,
  serviceAccessToken,
  whitelabelUrl,
  onPublishSuccess,
  showSnackbar,
  publishDisabled = false,
}) => {
  const { loading, runWithLoading } = useLoading();
  const siteUrl = normalizeSiteUrl(whitelabelUrl);

  const handlePublish = async () => {
    if (!charityId || !config?.adminServiceUrl || !serviceAccessToken) {
      showSnackbar?.("Unable to publish. Please try again later.", "error");
      return;
    }

    await runWithLoading(async () => {
      try {
        await publishCharityCustomization({
          charityId,
          config,
          serviceAccessToken,
        });
        showSnackbar?.(
          "Your site has been published! Changes will be live in a few minutes.",
          "success"
        );
        onPublishSuccess?.();
      } catch (error) {
        console.error("Failed to publish charity customization:", error);
        showSnackbar?.(
          "There was an error publishing your site. Please try again later.",
          "error"
        );
      }
    });
  };

  const openExternal = (url: string) => {
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const handlePreviewSite = () => {
    if (!siteUrl) {
      showSnackbar?.("Publish your site first to preview it.", "error");
      return;
    }
    openExternal(`${siteUrl}/preview`);
  };

  const handleLiveSite = () => {
    if (!siteUrl) {
      showSnackbar?.("Your live site URL is not available yet.", "error");
      return;
    }
    openExternal(siteUrl);
  };

  return (
    <Row className="justify-content-center mb-3">
      <Col md={10} lg={9}>
        <div className={s.siteActionBar}>
          {!publishDisabled && (
          <PrimaryButton
            variant="primary"
            onClick={handlePublish}
            loading={loading}
            loadingText="Publishing..."
            loadingAnimation="pulse"
            className={s.siteActionButton}
          >
            <>
              <FontAwesomeIcon icon={faArrowsRotate} size="sm" className="me-2" />
              Publish Site
            </>
          </PrimaryButton>
          )}
          <PrimaryButton
            variant="secondary"
            onClick={handlePreviewSite}
            disabled={!siteUrl}
            className={s.siteActionButton}
          >
            <>
              <SearchIcon sx={{ fontSize: 18, marginRight: "8px" }} />
              Preview Site
            </>
          </PrimaryButton>
          <PrimaryButton
            variant="secondary"
            onClick={handleLiveSite}
            disabled={!siteUrl}
            className={s.siteActionButton}
          >
            <>
              <PublicIcon sx={{ fontSize: 18, marginRight: "8px" }} />
              Live Site
            </>
          </PrimaryButton>
        </div>
      </Col>
    </Row>
  );
};

export default SiteActionButtons;
